import React, { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useOutletContext } from "react-router-dom";
import ProviderHeader from "./ProviderHeader";
import TransactionHistoryFilters from "./TransactionHistoryFilters";
import TransactionHistoryTable from "./TransactionHistoryTable";
import Pagination from "../../../Components/Common/Pagination";
import AutoPayConfirmationModal from "./AutoPayConfirmationModal";
import BillTransactionHistorySkeleton from "./BillTransactionHistorySkeleton";
import useBillTransactionHistory from "../hooks/useBillTransactionHistory";
import {
  restBillsData,
  updateOgeroInfoAndStatus,
} from "../../../../features/billsPageSlice";
import {
  setActionPayload,
  setActionType,
} from "../../../../features/authorizationSlice";
import { ACTION_TYPES } from "../../../../app/actionTypes";

export default function BillTransactionHistory({ onOpenQrAfterGenerate: onOpenQrAfterGenerateProp }) {
  const dispatch = useDispatch();
  const outletContext = useOutletContext();
  const onOpenQrAfterGenerate =
    onOpenQrAfterGenerateProp ||
    (Array.isArray(outletContext) ? outletContext[0] : null);
  const [autoPayState, setAutoPayState] = useState({
    isOpen: false,
    isEnabled: false,
    message: "",
    title: "",
    transactionId: null,
  });
  const {
    billsLoading,
    dateRange,
    dateString,
    error,
    fetchHistory,
    filteredTransactions,
    isAutomatable,
    provider,
    searchQuery,
    selectedPage,
    setDateRange,
    setSearchQuery,
    setSelectedPage,
    totalCount,
    totalPages,
  } = useBillTransactionHistory();

  const historyLabel = useMemo(() => {
    if (!totalCount) {
      return "0 transactions";
    }

    return `${totalCount} transaction${totalCount === 1 ? "" : "s"}`;
  }, [totalCount]);

  const handleBack = useCallback(() => {
    dispatch(restBillsData());
  }, []);

  const handlePageChange = useCallback(
    async (page) => {
      setSelectedPage(page);
      await fetchHistory(page, dateString.startDateString, dateString.endDateString);
    },
    [
      dateString.endDateString,
      dateString.startDateString,
    ],
  );

  const handleOpenAutoPay = useCallback(
    (transaction) => {
      const accountNumber =
        String(transaction?.Description || "").match(/\d+/)?.[0] || "";
      const nextIsEnabled = !transaction?.AutoEnabled;
      const providerTitle = provider?.title || "this";

      dispatch(
        updateOgeroInfoAndStatus({
          info: transaction?.Info,
          autoPaystatus: nextIsEnabled ? 1 : 2,
        }),
      );

      setAutoPayState({
        isEnabled: nextIsEnabled,
        isOpen: true,
        message: nextIsEnabled
          ? `Are you sure you want to enable autopay to settle the ${providerTitle} bill every month for ${accountNumber}?`
          : `Are you sure you want to disable autopay for the ${providerTitle} bill of ${accountNumber}?`,
        title: nextIsEnabled ? "Enable Auto-Pay" : "Disable Auto-Pay",
        transactionId: transaction?.Transaction,
      });
    },
    [provider?.title],
  );

  const handleConfirmAutoPay = useCallback(() => {
    const payload = {
      TransactionID: autoPayState.transactionId,
      IsEnabled: autoPayState.isEnabled,
      WebKey: sessionStorage.getItem("webKey"),
    };

    dispatch(setActionPayload(payload));
    dispatch(setActionType(ACTION_TYPES.UPDATE_AUTOMATIC_BILL_PAYMENT));

    if (typeof onOpenQrAfterGenerate === "function") {
      onOpenQrAfterGenerate();
    }

    setAutoPayState((currentValue) => ({
      ...currentValue,
      isOpen: false,
    }));
  }, [
    autoPayState.isEnabled,
    autoPayState.transactionId,
    onOpenQrAfterGenerate,
  ]);

  return (
    <section className="bills-page__history">
      <ProviderHeader provider={provider} onBack={handleBack} />

      {billsLoading ? (
        <BillTransactionHistorySkeleton />
      ) : (
        <div className="bills-page__history-panel">
          <div className="bills-page__section-header">
            <h2>Transaction History</h2>
            <span className="bills-page__results">{historyLabel}</span>
          </div>

          <TransactionHistoryFilters
            dateRange={dateRange}
            onDateChange={setDateRange}
            onSearchChange={setSearchQuery}
            searchQuery={searchQuery}
          />

          <TransactionHistoryTable
            error={error}
            errorMessage="Unable to load data right now."
            isAutomatable={isAutomatable}
            onRetry={() =>
              fetchHistory(
                selectedPage,
                dateString.startDateString,
                dateString.endDateString,
              )
            }
            onAutoPay={handleOpenAutoPay}
            transactions={filteredTransactions}
          />

          {!error && filteredTransactions.length > 0 ? (
            <Pagination
              currentPage={selectedPage}
              onPageChange={handlePageChange}
              totalPages={totalPages}
              totalCount={totalCount}
              className="bills-page__history-pagination"
            />
          ) : null}
        </div>
      )}

      <AutoPayConfirmationModal
        isOpen={autoPayState.isOpen}
        message={autoPayState.message}
        onClose={() =>
          setAutoPayState((currentValue) => ({
            ...currentValue,
            isOpen: false,
          }))
        }
        onConfirm={handleConfirmAutoPay}
        title={autoPayState.title}
      />
    </section>
  );
}
